import cx from 'classnames';
import { useCallback, type FunctionComponent } from 'react';

import BoardIcon from '@atlaskit/icon/core/board';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import InboxIcon from '@atlaskit/icon/core/inbox';
import { Analytics } from '@trello/atlassian-analytics';
import { intl } from '@trello/i18n';
import type { ShortcutHandler } from '@trello/keybindings';
import { getKey, Key, Scope, useChordedShortcut } from '@trello/keybindings';
import { getScreenFromUrl } from '@trello/marketing-screens';
import { SpotlightTransition } from '@trello/nachos/onboarding-experimental';
import { RouteId, useIsActiveRoute } from '@trello/router';
import type {
  BoardTestIds,
  InboxTestIds,
  PlannerTestIds,
} from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { useBoardIdFromBoardOrCardRoute } from 'app/src/components/Board/useBoardIdFromBoardOrCardRoute';
import {
  IslandNav,
  IslandNavButton,
  IslandNavSeparator,
} from 'app/src/components/IslandNav';
import {
  PANEL_NAVIGATION_SPOTLIGHT,
  usePersonalProductivitySpotlightTour,
} from 'app/src/components/PersonalProductivityOnboarding/usePersonalProductivitySpotlightTour';
import { ShortcutTooltip } from 'app/src/components/ShortcutTooltip';
import { BoardSwitcherButton } from './BoardSwitcherButton';

import styles from './PanelNavigation.less';

interface PanelNavigationProps {
  configuration: 'panels' | 'tabs';
  inbox: boolean;
  board: boolean;
  planner: boolean;
  isBoardOpenBlocked: boolean;
  toggleInbox: () => void;
  togglePlanner: () => void;
  toggleBoard: () => void;
}

export const PanelNavigation: FunctionComponent<PanelNavigationProps> = ({
  configuration,
  inbox,
  planner,
  board,
  isBoardOpenBlocked,
  toggleInbox,
  togglePlanner,
  toggleBoard,
}) => {
  const source = getScreenFromUrl();

  const isCardRoute = useIsActiveRoute(RouteId.CARD);

  const inboxToggleHandler = useCallback(() => {
    if (inbox) {
      Analytics.sendClosedComponentEvent({
        componentType: 'panel',
        componentName: 'inboxPanel',
        source,
        attributes: {
          isInboxOpen: inbox,
          isPlannerOpen: planner,
          isBoardOpen: board,
        },
      });
    } else {
      Analytics.sendViewedComponentEvent({
        componentType: 'panel',
        componentName: 'inboxPanel',
        source,
        attributes: {
          isInboxOpen: inbox,
          isPlannerOpen: planner,
          isBoardOpen: board,
        },
      });
    }

    toggleInbox();
  }, [board, inbox, planner, source, toggleInbox]);

  const plannerToggleHandler = useCallback(() => {
    if (planner) {
      Analytics.sendClosedComponentEvent({
        componentType: 'panel',
        componentName: 'plannerPanel',
        source,
        attributes: {
          isInboxOpen: inbox,
          isPlannerOpen: planner,
          isBoardOpen: board,
        },
      });
    } else {
      Analytics.sendViewedComponentEvent({
        componentType: 'panel',
        componentName: 'plannerPanel',
        source,
        attributes: {
          isInboxOpen: inbox,
          isPlannerOpen: planner,
          isBoardOpen: board,
        },
      });
    }

    togglePlanner();
  }, [board, inbox, planner, source, togglePlanner]);

  const boardToggleHandler = useCallback(() => {
    if (board) {
      Analytics.sendClosedComponentEvent({
        componentType: 'panel',
        componentName: 'boardPanel',
        source,
        attributes: {
          isInboxOpen: inbox,
          isPlannerOpen: planner,
          isBoardOpen: board,
        },
      });
    } else {
      Analytics.sendViewedComponentEvent({
        componentType: 'panel',
        componentName: 'boardPanel',
        source,
        attributes: {
          isInboxOpen: inbox,
          isPlannerOpen: planner,
          isBoardOpen: board,
        },
      });
    }

    toggleBoard();
  }, [board, inbox, planner, source, toggleBoard]);

  const onInboxButtonClick = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'inboxPanelButton',
      source,
      attributes: {
        isInboxOpen: inbox,
        isPlannerOpen: planner,
        isBoardOpen: board,
      },
    });

    inboxToggleHandler();
  };

  const onPlannerButtonClick = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'plannerPanelButton',
      source,
      attributes: {
        isInboxOpen: inbox,
        isPlannerOpen: planner,
        isBoardOpen: board,
      },
    });

    plannerToggleHandler();
  };

  const onBoardButtonClick = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'boardPanelButton',
      source,
      attributes: {
        isInboxOpen: inbox,
        isPlannerOpen: planner,
        isBoardOpen: board,
      },
    });

    boardToggleHandler();
  };

  const inboxShortcutHandler = useCallback(() => {
    Analytics.sendPressedShortcutEvent({
      shortcutName: 'inboxPanelShortcut',
      keyValue: 'g+i',
      source,
      attributes: {
        isInboxOpen: inbox,
        isPlannerOpen: planner,
        isBoardOpen: board,
      },
    });

    inboxToggleHandler();
  }, [board, inbox, inboxToggleHandler, planner, source]);

  const plannerShortcutHandler = useCallback(() => {
    Analytics.sendPressedShortcutEvent({
      shortcutName: 'plannerPanelShortcut',
      keyValue: 'g+p',
      source,
      attributes: {
        isInboxOpen: inbox,
        isPlannerOpen: planner,
        isBoardOpen: board,
      },
    });

    plannerToggleHandler();
  }, [board, inbox, planner, plannerToggleHandler, source]);

  const boardShortcutHandler = useCallback(() => {
    Analytics.sendPressedShortcutEvent({
      shortcutName: 'boardPanelShortcut',
      keyValue: 'g+b',
      source,
      attributes: {
        isInboxOpen: inbox,
        isPlannerOpen: planner,
        isBoardOpen: board,
      },
    });

    boardToggleHandler();
  }, [source, inbox, planner, board, boardToggleHandler]);

  const shortcutHandler = useCallback<ShortcutHandler>(
    (event) => {
      switch (getKey(event)) {
        case Key.b:
          boardShortcutHandler();
          break;
        case Key.i:
          inboxShortcutHandler();
          break;
        case Key.p:
          plannerShortcutHandler();
          break;
        default:
          break;
      }
    },
    [boardShortcutHandler, inboxShortcutHandler, plannerShortcutHandler],
  );

  useChordedShortcut(shortcutHandler, {
    scope: Scope.Global,
    key: Key.g,
  });

  const inboxIcon = (
    <InboxIcon
      label={intl.formatMessage({
        id: 'templates.split_screen.inbox',
        defaultMessage: 'Inbox',
        description: 'Inbox',
      })}
    />
  );

  const plannerIcon = (
    <CalendarIcon
      label={intl.formatMessage({
        id: 'templates.split_screen.planner',
        defaultMessage: 'Planner',
        description: 'Planner',
      })}
    />
  );

  const boardIcon = (
    <BoardIcon
      label={intl.formatMessage({
        id: 'templates.split_screen.board',
        defaultMessage: 'Board',
        description: 'Board',
      })}
    />
  );

  const hideSpotlight = configuration === 'tabs';

  const boardId = useBoardIdFromBoardOrCardRoute();
  const { renderActiveSpotlight } = usePersonalProductivitySpotlightTour({
    source: 'panelNavigation',
    hideSpotlight,
    boardId,
  });

  return (
    <IslandNav
      isFullWidth={configuration === 'tabs'}
      className={cx({
        [styles.configurationPanels]: configuration === 'panels',
        [styles.configurationTabs]: configuration === 'tabs',
        [styles.isCardRoute]: isCardRoute,
      })}
      spotlightTargetName={PANEL_NAVIGATION_SPOTLIGHT}
      hideSpotlight={hideSpotlight}
    >
      <ShortcutTooltip
        shortcutText={intl.formatMessage({
          id: 'templates.split_screen.inbox',
          defaultMessage: 'Inbox',
          description: 'Inbox',
        })}
        shortcutKey="g+i"
        position="top"
      >
        <IslandNavButton
          icon={inboxIcon}
          isDisabled={!planner && !board}
          isSelected={inbox}
          onClick={onInboxButtonClick}
          testId={getTestId<InboxTestIds>('panel-nav-inbox-button')}
        />
      </ShortcutTooltip>
      <ShortcutTooltip
        shortcutText={intl.formatMessage({
          id: 'templates.split_screen.planner',
          defaultMessage: 'Planner',
          description: 'Planner',
        })}
        shortcutKey="g+p"
        position="top"
      >
        <IslandNavButton
          icon={plannerIcon}
          isSelected={planner}
          isDisabled={!inbox && !board}
          onClick={onPlannerButtonClick}
          testId={getTestId<PlannerTestIds>('panel-nav-planner-button')}
        />
      </ShortcutTooltip>
      <ShortcutTooltip
        shortcutText={intl.formatMessage({
          id: 'templates.split_screen.board',
          defaultMessage: 'Board',
          description: 'Board',
        })}
        shortcutKey="g+b"
        position="top"
      >
        <IslandNavButton
          icon={boardIcon}
          isSelected={board}
          isDisabled={(!inbox && !planner) || isBoardOpenBlocked}
          onClick={onBoardButtonClick}
          testId={getTestId<BoardTestIds>('panel-nav-board-button')}
        />
      </ShortcutTooltip>
      <IslandNavSeparator />

      <BoardSwitcherButton />

      <SpotlightTransition>{renderActiveSpotlight()}</SpotlightTransition>
    </IslandNav>
  );
};
